import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useProfile } from "@/hooks/useAuth";
import { useAppStore } from "@/store/useAppStore";
import { useIsSolo } from "@/hooks/useIsSolo";
import { fetchCategories, fetchTransactionsWithLines, monthRange } from "@/lib/queries";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useFormatCurrency } from "@/hooks/useFormatCurrency";
import { BudgetBar } from "@/components/BudgetBar";
import { toast } from "sonner";
import { toastError } from "@/lib/errors";
import { isAdjustCategory } from "@/lib/categories";

export const Route = createFileRoute("/_app/budgets")({ component: BudgetsPage });

function BudgetsPage() {
  const formatCurrency = useFormatCurrency();
  const { profile } = useProfile();
  const hh = profile?.household_id;
  const { year, month, viewMode } = useAppStore();
  const { isSolo } = useIsSolo();
  const myId = profile?.id;
  const qc = useQueryClient();
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const catQ = useQuery({ queryKey: ["categories", hh], queryFn: () => fetchCategories(hh!), enabled: !!hh });
  // Reutiliza el cache del dashboard (mismo queryKey).
  const txQ = useQuery({
    queryKey: ["tx", hh, year, month],
    queryFn: () => fetchTransactionsWithLines(hh!, monthRange(year, month)),
    enabled: !!hh,
  });
  // Cargamos TODOS los presupuestos del hogar para poder heredar el último monto
  // fijado por categoría hasta que el usuario lo cambie.
  const budgetQ = useQuery({
    queryKey: ["budgets-all", hh],
    queryFn: async () => {
      const { data } = await supabase.from("budgets").select("*").eq("household_id", hh!);
      return data ?? [];
    },
    enabled: !!hh,
  });

  // Filtramos por scope: en personal solo presupuestos privados del usuario,
  // en hogar solo los compartidos. En modo monousuario mostramos todo (no
  // hay distinción real entre Hogar/Personal hasta que se sume la pareja).
  const scopeFiltered = useMemo(() => {
    const all = budgetQ.data ?? [];
    if (isSolo) return all;
    return all.filter((b: any) =>
      viewMode === "personal"
        ? (b.visibility === "private" && b.owner_id === myId)
        : b.visibility === "shared",
    );
  }, [budgetQ.data, viewMode, myId, isSolo]);

  // Para cada categoría calculamos el presupuesto efectivo del mes:
  // - exact: existe registro para (year, month) → editamos ese.
  // - inherited: usamos el más reciente con (year, month) <= actual → al guardar
  //   se crea un nuevo registro para el mes actual.
  type Effective = { categoryId: string; amount: number; source: "exact" | "inherited" | "none"; fromYear?: number; fromMonth?: number; existingId?: string };
  const effectiveByCategory = useMemo(() => {
    const m = new Map<string, Effective>();
    const byCat = new Map<string, any[]>();
    for (const b of scopeFiltered) {
      const arr = byCat.get(b.category_id) ?? [];
      arr.push(b);
      byCat.set(b.category_id, arr);
    }
    for (const [catId, arr] of byCat) {
      const exact = arr.find((b: any) => b.year === year && b.month === month);
      if (exact) {
        m.set(catId, { categoryId: catId, amount: Number(exact.monthly_amount), source: "exact", existingId: exact.id });
        continue;
      }
      const past = arr
        .filter((b: any) => b.year < year || (b.year === year && b.month < month))
        .sort((a: any, b: any) => (b.year - a.year) || (b.month - a.month));
      if (past.length > 0) {
        const p = past[0];
        m.set(catId, { categoryId: catId, amount: Number(p.monthly_amount), source: "inherited", fromYear: p.year, fromMonth: p.month });
      }
    }
    return m;
  }, [scopeFiltered, year, month]);

  // Helper de scope: misma regla que se aplica a presupuestos / líneas.
  // - household: solo categorías 'shared'
  // - personal:  solo 'private' del usuario actual
  // - solo:      todas
  function categoryInScope(c: any): boolean {
    if (isSolo) return true;
    if (viewMode === "personal") return c.visibility === "private" && c.owner_id === myId;
    return c.visibility === "shared";
  }

  // Excluimos las categorías de "Ajuste de Saldo" — no se presupuestan,
  // viven en el banner del Dashboard. Y filtramos por scope para evitar que
  // categorías privadas aparezcan en Hogar y viceversa.
  const allExpenseCats =
    catQ.data?.filter((c) => c.type === "expense" && !isAdjustCategory(c)) ?? [];
  const expenseCats = useMemo(
    () => allExpenseCats.filter(categoryInScope),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allExpenseCats, viewMode, myId, isSolo],
  );
  const hiddenByScope = allExpenseCats.length - expenseCats.length;

  // Mapa de gasto del mes por categoría (scope-aware, considera reembolsos).
  const catMap = useMemo(() => new Map((catQ.data ?? []).map((c) => [c.id, c])), [catQ.data]);
  const headerById = useMemo(
    () => new Map((txQ.data?.headers ?? []).map((h: any) => [h.id, h])),
    [txQ.data?.headers],
  );
  const visibleLines = useMemo(() => {
    const lines = txQ.data?.lines ?? [];
    if (isSolo) return lines;
    if (viewMode === "personal") return lines.filter((l: any) => l.visibility === "private" && l.owner_id === myId);
    return lines.filter((l: any) => l.visibility === "shared");
  }, [txQ.data?.lines, isSolo, viewMode, myId]);

  const spentByCategory = useMemo(() => {
    const m = new Map<string, number>();
    visibleLines.forEach((l: any) => {
      const c = catMap.get(l.category_id ?? "");
      if (c?.type !== "expense") return;
      const h = headerById.get(l.header_id) as any;
      const sign = h?.is_refund ? -1 : 1;
      m.set(l.category_id, (m.get(l.category_id) ?? 0) + sign * Number(l.amount));
    });
    return m;
  }, [visibleLines, catMap, headerById]);

  // Identificar categorías padre (las que tienen al menos una hija).
  const parentIds = useMemo(() => {
    const s = new Set<string>();
    for (const c of catQ.data ?? []) {
      if (c.parent_id) s.add(c.parent_id);
    }
    return s;
  }, [catQ.data]);

  // Mapa parent_id -> hijas (solo gastos)
  const childrenByParent = useMemo(() => {
    const m = new Map<string, typeof expenseCats>();
    for (const c of expenseCats) {
      if (c.parent_id) {
        const arr = m.get(c.parent_id) ?? [];
        arr.push(c);
        m.set(c.parent_id, arr);
      }
    }
    return m;
  }, [expenseCats]);

  // Gasto agregado para una categoría padre = suma de hijas.
  function getParentSpent(parentId: string): number {
    const children = childrenByParent.get(parentId) ?? [];
    let total = 0;
    for (const child of children) total += spentByCategory.get(child.id) ?? 0;
    return total;
  }

  // KPIs globales del scope: presupuesto total mensual definido + cobertura.
  const kpis = useMemo(() => {
    let monthlyBudget = 0;
    let coveredCount = 0;
    let leafCount = 0;
    for (const c of expenseCats) {
      if (parentIds.has(c.id)) continue; // solo hojas
      leafCount++;
      const eff = effectiveByCategory.get(c.id);
      const amt = eff?.amount ?? 0;
      if (amt > 0) {
        monthlyBudget += amt;
        coveredCount++;
      }
    }
    return {
      monthlyBudget,
      annualBudget: monthlyBudget * 12,
      coverage: leafCount > 0 ? (coveredCount / leafCount) * 100 : 0,
      coveredCount,
      leafCount,
    };
  }, [expenseCats, parentIds, effectiveByCategory]);

  // Reordenamos: padres con sus hijas debajo, luego huérfanas.
  const orderedRows = useMemo(() => {
    const rows: { cat: typeof expenseCats[number]; depth: number; isParent: boolean }[] = [];
    const seen = new Set<string>();
    // Padres primero
    for (const c of expenseCats) {
      if (parentIds.has(c.id) && !c.parent_id) {
        rows.push({ cat: c, depth: 0, isParent: true });
        seen.add(c.id);
        for (const child of childrenByParent.get(c.id) ?? []) {
          rows.push({ cat: child, depth: 1, isParent: false });
          seen.add(child.id);
        }
      }
    }
    // Categorías sin hijos y sin padre (hojas top-level)
    for (const c of expenseCats) {
      if (!seen.has(c.id) && !c.parent_id && !parentIds.has(c.id)) {
        rows.push({ cat: c, depth: 0, isParent: false });
        seen.add(c.id);
      }
    }
    // Hijas huérfanas (parent que no está en la lista filtrada)
    for (const c of expenseCats) {
      if (!seen.has(c.id)) {
        rows.push({ cat: c, depth: 1, isParent: false });
      }
    }
    return rows;
  }, [expenseCats, parentIds, childrenByParent]);

  // Suma derivada para una categoría padre = sum(presupuesto efectivo de hijas)
  // considerando drafts pendientes de guardar.
  function getParentDerivedAmount(parentId: string): number {
    const children = childrenByParent.get(parentId) ?? [];
    let total = 0;
    for (const child of children) {
      const draft = drafts[child.id];
      if (draft !== undefined) {
        const n = parseFloat(draft);
        if (!isNaN(n)) total += n;
      } else {
        const eff = effectiveByCategory.get(child.id);
        if (eff) total += eff.amount;
      }
    }
    return total;
  }

  function onChangeAmount(categoryId: string, value: string) {
    setDrafts((d) => ({ ...d, [categoryId]: value }));
  }

  function discardDrafts() {
    setDrafts({});
  }

  async function saveAll() {
    if (!hh || !myId) return;
    // En modo monousuario forzamos shared para evitar el bug de "lo creo y no se ve".
    const visibility: "private" | "shared" = isSolo ? "private" : (viewMode === "personal" ? "private" : "shared");
    const owner_id = isSolo ? myId : (viewMode === "personal" ? myId : null);
    const entries = Object.entries(drafts);
    if (entries.length === 0) return;
    setSaving(true);
    try {
      // UPSERT real para evitar el conflicto duplicate key cuando ya existe
      // una fila para (household, category, year, month) — p.ej. tras un save
      // concurrente o cuando "inherited" se materializa entre renders.
      const payload = entries
        .map(([categoryId, raw]) => ({ categoryId, amount: parseFloat(raw) }))
        .filter((e) => !isNaN(e.amount) && !parentIds.has(e.categoryId))
        .map((e) => ({
          household_id: hh,
          category_id: e.categoryId,
          year, month,
          monthly_amount: e.amount,
          visibility,
          owner_id,
        }));
      if (payload.length > 0) {
        const { error } = await supabase
          .from("budgets")
          .upsert(payload, { onConflict: "household_id,category_id,year,month" });
        if (error) throw error;
      }
      await qc.invalidateQueries({ queryKey: ["budgets-all", hh] });
      setDrafts({});
      toast.success(`${entries.length} presupuesto(s) guardado(s)`);
    } catch (e: any) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  }

  const dirtyCount = Object.keys(drafts).length;
  const scopeLabel = isSolo ? "Tus presupuestos" : (viewMode === "personal" ? "🧍 Personales" : "🏠 del Hogar");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Presupuestos · {month}/{year}</h1>
        <Badge variant="secondary">{scopeLabel}</Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        Los montos que fijes se mantendrán automáticamente mes a mes hasta que los modifiques.
        {!isSolo && <> Cambia entre <b>Hogar</b> y <b>Personal</b> con el toggle del menú.</>}
      </p>
      {!isSolo && hiddenByScope > 0 && (
        <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          {hiddenByScope} categoría(s) ocultas por el scope actual
          ({viewMode === "personal" ? "🧍 Personales" : "🏠 Hogar"}).
          Cambiá el toggle para verlas.
        </div>
      )}

      <Card>
        <CardContent className="grid gap-4 p-4 sm:grid-cols-3">
          <div>
            <p className="text-xs text-muted-foreground">Presupuesto mensual total</p>
            <p className="text-xl font-bold tabular-nums">{formatCurrency(kpis.monthlyBudget)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Proyección anual (×12)</p>
            <p className="text-xl font-bold tabular-nums">{formatCurrency(kpis.annualBudget)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Cobertura</p>
            <p className="text-xl font-bold tabular-nums">
              {kpis.coverage.toFixed(0)}%{" "}
              <span className="text-xs font-normal text-muted-foreground">
                ({kpis.coveredCount}/{kpis.leafCount} categorías)
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Por categoría</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {orderedRows.map(({ cat: c, depth, isParent }) => {
            if (isParent) {
              const derived = getParentDerivedAmount(c.id);
              const parentSpent = getParentSpent(c.id);
              return (
                <div key={c.id} className="border-b py-2 bg-muted/30 -mx-2 px-2 rounded space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2 min-w-0">
                      <span className="inline-block h-3 w-3 rounded-full shrink-0" style={{ background: c.color }} />
                      <span className="truncate font-semibold">{c.name}</span>
                    </span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">suma de subcategorías</Badge>
                      <span className="w-32 text-right text-sm font-semibold tabular-nums">
                        {formatCurrency(derived)}
                      </span>
                    </div>
                  </div>
                  <BudgetBar variant="compact" spent={parentSpent} budget={derived} />
                </div>
              );
            }
            const eff = effectiveByCategory.get(c.id);
            const draft = drafts[c.id];
            const isDirty = draft !== undefined;
            const displayValue = isDirty ? draft : (eff ? String(eff.amount) : "");
            // El bar usa el monto del draft si lo hay, sino el efectivo.
            const liveBudget = isDirty
              ? (parseFloat(draft) || 0)
              : (eff?.amount ?? 0);
            const spent = spentByCategory.get(c.id) ?? 0;
            return (
              <div
                key={c.id}
                className={"border-b py-2 space-y-2 " + (isDirty ? "bg-primary/5 -mx-2 px-2 rounded" : "")}
                style={depth > 0 ? { paddingLeft: 16 } : undefined}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="flex items-center gap-2 min-w-0">
                    {depth > 0 && <span className="text-muted-foreground text-xs">↳</span>}
                    <span className="inline-block h-3 w-3 rounded-full shrink-0" style={{ background: c.color }} />
                    <span className="truncate">{c.name}</span>
                  </span>
                  <div className="flex items-center gap-2">
                    {!isDirty && eff?.source === "inherited" && (
                      <Badge variant="outline" className="text-[10px]">heredado {eff.fromMonth}/{eff.fromYear}</Badge>
                    )}
                    {!isDirty && !eff && (
                      <span className="text-xs text-muted-foreground">Sin presupuesto</span>
                    )}
                    {isDirty && <span className="text-[10px] text-primary">• sin guardar</span>}
                    <Input
                      type="number"
                      step="0.01"
                      value={displayValue}
                      onChange={(e) => onChangeAmount(c.id, e.target.value)}
                      className="w-32"
                    />
                  </div>
                </div>
                <BudgetBar variant="compact" spent={spent} budget={liveBudget} />
              </div>
            );
          })}
        </CardContent>
      </Card>

      {dirtyCount > 0 && (
        <div className="sticky bottom-4 z-10 flex items-center justify-end gap-2 rounded-lg border bg-background/95 p-3 shadow-md backdrop-blur">
          <span className="mr-auto text-sm text-muted-foreground">{dirtyCount} cambio(s) pendiente(s)</span>
          <Button variant="ghost" onClick={discardDrafts} disabled={saving}>Descartar</Button>
          <Button onClick={saveAll} disabled={saving}>
            {saving ? "Guardando…" : `Guardar ${dirtyCount} cambio(s)`}
          </Button>
        </div>
      )}
      {/* Mantenemos formatCurrency como utilidad por si se reincorpora visualmente. */}
      <span className="hidden">{formatCurrency(0)}</span>
    </div>
  );
}
